import { interpolate, useAnimatedStyle } from 'react-native-reanimated';

import type { SharedValue } from 'react-native-reanimated';

type SizeInput = number | SharedValue<number>;

type Use3DRotationStyleParams = {
  x: SharedValue<number>;
  y: SharedValue<number>;
  maxSize: SizeInput;
  maxRotation: number;
  perspective?: number;
  /** When set, horizontal input maps to `[0, interpolateXMax]`; defaults to `maxSize` (unchanged 300×300 demo). */
  interpolateXMax?: SizeInput;
  /** When set, vertical input maps to `[0, interpolateYMax]`; defaults to `maxSize`. */
  interpolateYMax?: SizeInput;
};

const resolve_size = (size: SizeInput, fallback: number): number => {
  'worklet';
  if (typeof size === 'number') {
    return size > 0 ? size : fallback;
  }
  const v = size.value;
  return v > 0 ? v : fallback;
};

// If you want to fully understand the math behind this function,
// I've made in the past a video about it: https://youtu.be/pVesCl7TY8A
const use3DRotationStyle = ({
  x,
  y,
  maxSize,
  maxRotation,
  perspective = 500,
  interpolateXMax,
  interpolateYMax,
}: Use3DRotationStyleParams) => {
  // NOTE: We use x.value instead of y.value
  const rStyle = useAnimatedStyle(() => {
    const size_fallback = resolve_size(maxSize, 360);
    const y_max = resolve_size(interpolateYMax ?? maxSize, size_fallback);
    const x_max = resolve_size(interpolateXMax ?? maxSize, size_fallback);

    const rotateX = interpolate(
      y.value,
      [0, y_max],
      [maxRotation, -maxRotation],
    );

    // NOTE: We use x.value instead of y.value
    const rotateY = interpolate(
      x.value,
      [0, x_max],
      [-maxRotation, maxRotation],
    );

    return {
      transform: [
        { perspective: perspective },
        { rotateX: `${rotateX}deg` },
        { rotateY: `${rotateY}deg` },
      ],
    };
  }, [maxSize, maxRotation, interpolateYMax, interpolateXMax, perspective, x, y]);

  return {
    rRotationStyle: rStyle,
  };
};

export { use3DRotationStyle };
